import { Skeleton } from '@/shared/components/ui/skeleton';
import { Star } from 'lucide-react';
import { NavigatorReasonHint } from '@/shared/components/navigator-results';
import { DeliveryModeChips } from '@/shared/components/navigator-results/DeliveryModeChips';
import type { DeliveryMode } from '@/shared/components/navigator-results/navigator-results.types';
import type { useFpQaProviderProfiles } from '../../hooks/useFpProviderProfiles';

type AltMatchRowProps = {
  query: ReturnType<typeof useFpQaProviderProfiles>[number];
  specialtyName: string;
  /** Feature 080 — AI rationale, shown in a tooltip behind the sparkles hint. */
  aiReason?: string | null;
  /** Amber tag: this row is the AI top match displaced by a user swap. */
  isTopMatchTag?: boolean;
  /** PROTOTYPE (087 ticket 10) — provider is in the global Best Match set. */
  isBestMatch?: boolean;
  /** Promotes this provider to the specialty's single selection. */
  onSelect?: () => void;
  /** Blocks the Select action while another swap is still persisting. */
  selectDisabled?: boolean;
  /** Live per-specialty location from the navigator match — not directory union. */
  location?: string | null;
  /** Delivery modes for the searched specialty (108 ticket 02). Compact chips. */
  specialtyDeliveryModes?: DeliveryMode[];
};

const AVATAR_PALETTE = [
  'bg-sage',
  'bg-[hsl(169_50%_30%)]',
  'bg-[hsl(192_40%_35%)]',
  'bg-[hsl(215_30%_32%)]',
  'bg-[hsl(215_18%_45%)]',
];

export const AltMatchRow = ({
  query,
  specialtyName,
  aiReason = null,
  isTopMatchTag = false,
  isBestMatch = false,
  onSelect,
  selectDisabled = false,
  location: liveLocation = null,
  specialtyDeliveryModes,
}: AltMatchRowProps) => {
  if (query.isLoading) {
    return <Skeleton className="h-16 w-full rounded-xl" />;
  }
  if (query.isError || !query.data) {
    return null;
  }

  const profile = query.data;
  const personName = [profile.user?.firstName, profile.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const companyLabel = profile.companyName?.trim() || '';
  const displayName = personName || companyLabel || 'Provider';
  const role =
    personName && companyLabel
      ? companyLabel
      : profile.professionalTitle || specialtyName;
  const initials = getInitials(displayName);
  const avatarColor = AVATAR_PALETTE[hashString(displayName) % AVATAR_PALETTE.length];
  const location = liveLocation?.trim() || '';
  const rating =
    typeof profile.averageRating === 'number' && profile.averageRating > 0
      ? profile.averageRating.toFixed(1)
      : null;

  return (
    <div className="flex items-center gap-4 rounded-xl border border-border-subtle bg-card-base px-4 py-3 transition-shadow duration-150 ease-out motion-reduce:transition-none hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor}`}
        aria-hidden
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <span className="truncate">{displayName}</span>
          {isTopMatchTag && (
            <span
              title="Highest-ranked provider for this specialty."
              className="flex-shrink-0 rounded-md bg-[hsl(40_75%_88%)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[hsl(35_55%_28%)]"
            >
              Top match
            </span>
          )}
          {/* PROTOTYPE (087 ticket 10) — same gold-foil family as the
              featured card's Best Match badge. */}
          {isBestMatch && (
            <span
              title="Highest-scoring provider across this care plan. Primary specialty breaks score ties."
              className="flex-shrink-0 rounded-md border border-[hsl(38_55%_68%)]/60 bg-gradient-to-br from-[hsl(43_75%_94%)] to-[hsl(30_68%_78%)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[hsl(30_65%_26%)]"
            >
              Best Match
            </span>
          )}
        </p>
        <p className="flex flex-wrap items-center text-xs text-text-secondary">
          <span className="truncate">{role}</span>
          {location && (
            <>
              <span className="mx-1.5 text-text-tertiary">·</span>
              {location}
            </>
          )}
          {specialtyDeliveryModes?.length ? (
            <span className="mx-1.5 text-text-tertiary">·</span>
          ) : null}
          <DeliveryModeChips modes={specialtyDeliveryModes} compact />
        </p>
      </div>
      {aiReason?.trim() && <NavigatorReasonHint reason={aiReason} />}
      {rating && (
        <span className="flex items-center gap-1 text-xs text-text-secondary">
          <Star className="h-3.5 w-3.5 fill-sage text-sage" aria-hidden />
          <span className="text-text-primary">{rating}</span>
        </span>
      )}
      {onSelect && (
        <button
          type="button"
          onClick={onSelect}
          disabled={selectDisabled}
          className="rounded-full border border-sage px-3 py-1.5 text-xs font-medium text-sage transition-colors duration-150 ease-out hover:bg-sage-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
        >
          Select
        </button>
      )}
      <a
        href={`/providers/${profile.id}?discoverySource=FP_QA`}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-full border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-primary transition-colors duration-150 ease-out hover:border-sage hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
      >
        View
      </a>
    </div>
  );
};

const getInitials = (name: string): string => {
  const parts = name.replace(/[^a-zA-Z\s]/g, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const hashString = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};
